import React from "react";
import CertificateFooter from "./CertificateFooter";

const formatShortMonthDate = (value) => {
  if (!value) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleDateString("en-US", { month: "short" });
  return `${day}-${month}-${date.getFullYear()}`;
};

/**
 * A4 preview for the Diesel Power Generator Emission Test Report.
 * Shared header / sign-off / footer; no disclaimer band.
 */
const EmissionTestDocument = ({ doc }) => {
  if (!doc) return null;
  const assetsBase = doc.assetsBase || "";
  const logos = doc.footerLogos || [];
  const rows = Array.isArray(doc.emission_rows) ? doc.emission_rows : [];

  const equipmentRows = [
    ["Report No.", doc.report_no],
    ["Client", doc.client],
    ["Model", doc.model],
    ["Sr. No.", doc.serial_no],
    ["Manufacturer/Make", doc.manufacturer],
    ["Capacity", doc.capacity],
    ["Nature of Power", doc.nature_of_power],
    ["Fuel Type", doc.fuel_type],
    ["Emission Standard", doc.emission_standard],
    ["Location", doc.location],
    ["Date of Test", formatShortMonthDate(doc.date_of_test || doc.issue_date)],
    ["Ambient Temperature", doc.ambient_temperature],
    ["Humidity", doc.humidity],
  ];

  return (
    <div className="cc-pdf-page cc-emit-page">
      <div className="cc-pdf-header">
        <img className="cc-pdf-logo" src={`${assetsBase}/logo.jpg`} alt="TUV PAK" />
        <div className="cc-pdf-header-text">
          <strong>TUV PAK INSPEC</strong>
          <span>TECHANICAL UNIQUE VISION PAKISTAN INSPECTION &amp; CERTIFICATION</span>
        </div>
      </div>
      <img className="cc-pdf-rule" src={`${assetsBase}/header-bar.png`} alt="" />

      <div className="cc-emit-title">{doc.report_title || "EMISSION TEST REPORT"}</div>
      <div className="cc-emit-subtitle">{doc.equipment_type || "Diesel Power Generator"}</div>

      <h5 className="cc-emit-section">1. Equipment Details</h5>

      <table className="cc-emit-equip">
        <tbody>
          {equipmentRows.map(([label, value]) => (
            <tr key={label}>
              <th>{label}</th>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h5 className="cc-emit-section">2. Test Methodology:</h5>
      <p className="cc-emit-para">{doc.test_methodology}</p>

      <h5 className="cc-emit-section">3. Emission Measurement Results</h5>
      <table className="cc-emit-results">
        <thead>
          <tr>
            <th>Parameter(s)</th>
            <th>Unit(s)</th>
            <th>Methodology</th>
            <th>Test Result(s)</th>
            <th>NEQS</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td>{row.parameter}</td>
              <td>{row.unit}</td>
              <td>{row.methodology}</td>
              <td>{row.test_result}</td>
              <td>{row.neqs}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h5 className="cc-emit-section">4. Test Equipment Details</h5>
      <ul className="cc-emit-bullets">
        <li>Smoke Meter Used {doc.smoke_meter}</li>
        <li>Calibration Date: {formatShortMonthDate(doc.calibration_date) || doc.calibration_date}</li>
        <li>Validity: {formatShortMonthDate(doc.calibration_validity || doc.expire_date)}</li>
        <li>Serial No. {doc.meter_serial_no}</li>
      </ul>

      <h5 className="cc-emit-section">5. Remarks and Conclusion</h5>
      <p className="cc-emit-para">{doc.remarks_conclusion}</p>

      <table className="cc-pdf-sign">
        <thead>
          <tr>
            <th>Inspected By</th>
            <th>Approved By</th>
            <th>Company Seal</th>
            <th>QR Code</th>
          </tr>
        </thead>
        <tbody>
          <tr className="cc-pdf-sign-names">
            <td>{doc.inspected_by_name}</td>
            <td>{doc.approved_by_name}</td>
            <td rowSpan={2} className="cc-pdf-sign-seal">
              {doc.sealUrl && <img src={doc.sealUrl} alt="Company seal" />}
            </td>
            <td rowSpan={2} className="cc-pdf-sign-qr">
              {doc.qrDataUrl && <img src={doc.qrDataUrl} alt="QR code" />}
            </td>
          </tr>
          <tr className="cc-pdf-sign-images">
            <td>{doc.inspectedSignatureUrl && <img src={doc.inspectedSignatureUrl} alt="Inspector signature" />}</td>
            <td>{doc.approvedSignatureUrl && <img src={doc.approvedSignatureUrl} alt="Approver signature" />}</td>
          </tr>
        </tbody>
      </table>

      <CertificateFooter
        assetsBase={assetsBase}
        logos={logos}
        website={doc.footer_website}
        email={doc.footer_email}
        gmail={doc.footer_gmail}
      />
    </div>
  );
};

export default EmissionTestDocument;
